import { FC, type JSX, useEffect, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import { FlowRadioGroup, Layout, NextButton, StepHeader, getRadioGroupNameProps } from '@eneco/flows/components';
import { requiredRadioChoice } from '@eneco/flows/helpers';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { RadioTile } from '@eneco/sparky';
import { ElectricalOutlet, SolarPanel } from '@eneco/sparky/illustrations';
import { usePlaceholderContent } from '@sitecore/common';
import { SolarPanelsChoiceStepRendering } from '@sitecore/types/SolarPanelsChoiceStep';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowConsumerContextEnergy } from '../../../context';
import { useFlowNavigation } from '../../hooks';

interface FormFields {
  hasSolarPanels: string;
}

const generateInputElement = (value: string, label: string, description?: string): JSX.Element => {
  let icon: JSX.Element;

  if (value === 'hasPanels') {
    icon = <SolarPanel size="small" />;
  } else {
    icon = <ElectricalOutlet size="small" />;
  }
  return (
    <RadioTile key={value} value={value} icon={icon} subtitle={description || undefined}>
      {label}
    </RadioTile>
  );
};

export const SolarPanelsStep: FC = () => {
  const { useFlowSelector } = useFlowHooks<FlowConsumerContextEnergy>();
  const flowState = useFlowSelector(state => state);
  const { updateContextAndGoToNextStep } = useFlowNavigation();

  const {
    context: { hasSolarPanels },
  } = flowState;

  const { SolarPanelsChoiceStep: { fields: textualData } = {} } = usePlaceholderContent<{
    SolarPanelsChoiceStep: SolarPanelsChoiceStepRendering;
  }>();

  const [shouldShowOfferAvailabilityNotification, setShouldShowOfferAvailabilityNotification] = useState(false);

  const formSchema = yup.object({
    hasSolarPanels: requiredRadioChoice(textualData?.choice?.hasSolarPanelsRadio?.value?.requiredMessage),
  });

  const toSolarPanelsValue = (hasPanels?: boolean) => {
    if (hasPanels === undefined) return undefined;

    return hasPanels ? 'hasPanels' : 'noPanels';
  };

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      hasSolarPanels: toSolarPanelsValue(hasSolarPanels),
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const watchHasSolarPanels = watch('hasSolarPanels');

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();

      updateContextAndGoToNextStep({
        hasSolarPanels: formData.hasSolarPanels === 'hasPanels',
      });
    }
  }, [isSubmitSuccessful]);

  useEffect(() => {
    setShouldShowOfferAvailabilityNotification(
      watchHasSolarPanels === 'hasPanels' &&
        !!textualData?.notifications?.offerAvailabilityWarningNotification?.value?.content,
    );
  }, [watchHasSolarPanels]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <StepHeader textualData={textualData} />
      <FlowRadioGroup
        control={control}
        name="hasSolarPanels"
        {...getRadioGroupNameProps(textualData?.choice?.hasSolarPanelsRadio?.value?.label)}
        hint={textualData?.choice?.hasSolarPanelsRadio?.value?.hint || undefined}
        direction="column"
        error={errors['hasSolarPanels']?.message}>
        {textualData?.choice?.hasSolarPanelsRadio?.value?.options?.map(option =>
          generateInputElement(option.name, option.label, option.description),
        )}
      </FlowRadioGroup>

      {shouldShowOfferAvailabilityNotification && (
        <TrackedNotificationBox
          isAlert={false}
          label={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.title}
          title={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.title ?? ''}
          text={<RichText html={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.content} />}
          variant={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.variant}
        />
      )}

      <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
    </Layout.Content>
  );
};
